import { useEffect, useMemo, useState } from 'react'
import { Search, Loader2, X, Stethoscope, Ban, UserPlus } from 'lucide-react'
import { cachedGet } from '../lib/api.js'
import { usePersistedState } from '../lib/persist.js'
import { nameInitial } from '../lib/text.js'
import { Segmented } from './statistic/statShared.jsx'
import SearchableSelect from '../components/SearchableSelect.jsx'
import {
  activeInfo, specList, specOptions, doctorCode, doctorFullName,
  exceptList, filterDoctors, byCode,
} from '../lib/dentist.js'
import DentistDrawer from '../components/dentist/DentistDrawer.jsx'
import DentistCreateDialog from '../components/dentist/DentistCreateDialog.jsx'
import { useMe } from '../lib/me.js'
import { canEditDentist } from '../lib/role.js'

// Manager › ทันตแพทย์ — the doctor directory. ONE fetch of the whole (projected)
// list, then search / spec / active are applied in memory, so every keystroke is
// instant and no filter can be silently truncated by a server page. See
// lib/dentist.js for why the Xano-side filters are not used.
//
// Default view is ใช้งาน: the table carries 600+ records but only ~400 are active,
// the rest being legacy doctors nobody is looking for.
const ACTIVE_OPTS = [
  { v: 'true',  label: 'ใช้งาน' },
  { v: 'false', label: 'ปิดใช้งาน' },
  { v: '',      label: 'ทั้งหมด' },
]

export default function Dentist() {
  const [all, setAll] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [open, setOpen] = useState(null) // the clicked row (slim record)
  const [createOpen, setCreateOpen] = useState(false)
  const me = useMe()
  const canCreate = canEditDentist(me)

  const [search, setSearch] = usePersistedState('dentist:search', '')
  const [specs, setSpecs] = usePersistedState('dentist:spec', () => new Set())
  const [active, setActive] = usePersistedState('dentist:active', 'true')

  useEffect(() => {
    let alive = true
    cachedGet('/api/doctors', { slim: 1 })
      .then((r) => { if (alive) setAll(Array.isArray(r) ? r : (r?.items || [])) })
      .catch((e) => { if (alive) setErr(String(e.message || e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  // Spec options come from the data, so a spec added in Xano shows up here with
  // no code change. Counts follow the ACTIVE filter (but not search/spec) — the
  // number next to a chip answers "how many doctors would this give me".
  const specPool = useMemo(
    () => filterDoctors(all, { active }),
    [all, active],
  )
  const options = useMemo(() => specOptions(specPool), [specPool])

  // A spec that disappears from the pool (e.g. only inactive doctors had it)
  // would otherwise filter everything to nothing with no visible chip to unset.
  useEffect(() => {
    if (!specs.size) return
    const live = new Set(options.map((o) => o.value))
    const kept = [...specs].filter((s) => live.has(s))
    if (kept.length !== specs.size) setSpecs(new Set(kept))
  }, [options]) // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(
    () => filterDoctors(all, { q: search, specs, active }).sort(byCode),
    [all, search, specs, active],
  )

  const hasFilter = search || specs.size > 0 || active !== 'true'
  function clearAll() { setSearch(''); setSpecs(new Set()); setActive('true') }

  return (
    // Heading + tabs live in DentistLayout; this is just the รายชื่อ tab body.
    <div className="max-w-[1200px]">
      <p className="text-[13.5px] text-slate-500 mb-4">ข้อมูลหมอ ความเชี่ยวชาญ และสถานะการทำงาน · กดที่แถวเพื่อดูรายละเอียด</p>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2.5 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาชื่อ / ชื่อเล่น / เลขที่ใบประกอบ / เบอร์โทร…"
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10"
          />
        </div>
        <SearchableSelect
          multiple
          className="w-[240px]"
          options={options}
          values={specs}
          onChange={setSpecs}
          placeholder="ความเชี่ยวชาญทั้งหมด"
          searchPlaceholder="ค้นหาความเชี่ยวชาญ…"
        />
        <Segmented value={active} onChange={setActive} options={ACTIVE_OPTS} />
        {hasFilter && (
          <button onClick={clearAll} className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100">
            <X size={14} /> ล้าง
          </button>
        )}
        {/* Manager / GM / Admin only — the same set that may edit a doctor, and the
            same the POST re-checks. Pushed to the right so it never sits between
            two filters. */}
        {canCreate && (
          <button
            onClick={() => setCreateOpen(true)}
            className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700"
          >
            <UserPlus size={15} /> เพิ่มทันตแพทย์
          </button>
        )}
      </div>

      {/* chosen specs, so a filter is never hidden behind a collapsed dropdown */}
      {specs.size > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {[...specs].map((s) => {
            const o = options.find((x) => x.value === s)
            return (
              <button
                key={s}
                onClick={() => setSpecs((prev) => { const n = new Set(prev); n.delete(s); return n })}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-300 bg-brand-50 px-3 py-1 text-[12.5px] font-medium text-brand-700"
              >
                {o?.label || s}
                <X size={12} className="text-brand-400" />
              </button>
            )
          })}
        </div>
      )}

      <div className="text-[13px] text-slate-500 mb-2">
        แสดง <span className="font-semibold text-slate-700 tabular-nums">{rows.length.toLocaleString()}</span> จาก {all.length.toLocaleString()} คน
      </div>

      {/* table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-left text-[12px] font-medium text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2.5">ทันตแพทย์</th>
                <th className="px-4 py-2.5">ความเชี่ยวชาญ</th>
                <th className="px-4 py-2.5">ไม่รับเคส</th>
                <th className="px-4 py-2.5">ติดต่อ</th>
                <th className="px-4 py-2.5 whitespace-nowrap w-px">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…</td></tr>
              )}
              {!loading && err && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-rose-500">{err}</td></tr>
              )}
              {!loading && !err && !rows.length && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">ไม่พบทันตแพทย์ที่ตรงกับตัวกรอง</td></tr>
              )}
              {!loading && !err && rows.map((d) => <Row key={d.id} d={d} onOpen={() => setOpen(d)} />)}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <DentistDrawer
          row={open}
          onClose={() => setOpen(null)}
          onSaved={(updated) => {
            if (!updated?.id) return
            // Patch the row in place rather than refetching — the drawer already
            // holds the server's response, and the cached /api/doctors list has
            // been invalidated for whoever reads it next.
            setAll((prev) => prev.map((x) => (Number(x.id) === Number(updated.id) ? { ...x, ...updated } : x)))
            setOpen((prev) => (prev && Number(prev.id) === Number(updated.id) ? { ...prev, ...updated } : prev))
          }}
        />
      )}

      {createOpen && (
        <DentistCreateDialog
          specOptions={options}
          onClose={() => setCreateOpen(false)}
          onCreated={(rec) => {
            setCreateOpen(false)
            if (!rec?.id) return
            // Prepended rather than refetched, same as the drawer's onSaved: the
            // POST already returned the server's record.
            setAll((prev) => (prev.some((x) => Number(x.id) === Number(rec.id)) ? prev : [rec, ...prev]))
            // …but unlike an edit, this row is INACTIVE, and the list defaults to
            // ใช้งาน. Landing back on a list the doctor is missing from reads as a
            // failed create, so the filter is widened to ทั้งหมด to show them
            // sitting in ปิดใช้งาน, waiting on their email.
            setActive('')
          }}
        />
      )}
    </div>
  )
}

function Row({ d, onOpen }) {
  const a = activeInfo(d.active === true)
  const spec = specList(d)
  const except = exceptList(d)
  return (
    <tr onClick={onOpen} className="border-b border-slate-50 last:border-0 hover:bg-brand-50/40 cursor-pointer transition">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <Avatar src={d.profile_img} name={doctorCode(d)} />
          <div className="min-w-0">
            <div className="font-medium text-slate-800 truncate">{doctorCode(d)}</div>
            <div className="text-[12px] text-slate-400 truncate">{d.name_eng || doctorFullName(d)}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {spec.length ? (
          <div className="flex flex-wrap gap-1">
            {spec.slice(0, 4).map((s) => (
              <span key={s} className="inline-flex items-center rounded-md bg-brand-50 text-brand-700 px-1.5 py-0.5 text-[11px]">{s}</span>
            ))}
            {spec.length > 4 && <span className="text-[11px] text-slate-400">+{spec.length - 4}</span>}
          </div>
        ) : <span className="text-slate-300">—</span>}
      </td>
      {/* The actual treatments, not a count — a manager scanning the list wants to
          see WHICH cases a doctor turns away. Three fit; the rest fold into +N
          (full list on hover). */}
      <td className="px-4 py-3">
        {except.length ? (
          <div className="flex flex-wrap items-center gap-1" title={except.join(' · ')}>
            <Ban size={11} className="text-amber-500 shrink-0" />
            {except.slice(0, 3).map((e, i) => (
              <span key={`${e}-${i}`} className="inline-flex items-center rounded-md bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[11px]">{e}</span>
            ))}
            {except.length > 3 && <span className="text-[11px] text-slate-400">+{except.length - 3}</span>}
          </div>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-4 py-3 text-slate-600 tabular-nums">{d.phone || '—'}</td>
      <td className="px-4 py-3 whitespace-nowrap w-px">
        <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${a.tone}`}>{a.label}</span>
      </td>
    </tr>
  )
}

function Avatar({ src, name }) {
  const [broken, setBroken] = useState(false)
  if (src && !broken) {
    return <img src={src} alt="" onError={() => setBroken(true)} className="w-9 h-9 rounded-full object-cover ring-1 ring-slate-200 shrink-0" />
  }
  return (
    <span className="w-9 h-9 rounded-full bg-brand-50 text-brand-600 text-[13.5px] font-semibold flex items-center justify-center shrink-0">
      {name ? nameInitial(name) : <Stethoscope size={15} />}
    </span>
  )
}
